import { basicBlockActions } from 'articleEditor/utils/actions';
import cx from 'classnames';
import { captionGlobalStore } from 'lib-knowledge-document-structure/plate/caption';
import {
  findNodePath,
  select,
  setNodes,
  useEditableProps,
} from 'lib-knowledge-document-structure/plate/common';
import {
  Resizable,
  ResizableProvider,
} from 'lib-knowledge-document-structure/plate/resizable';
import { useMyEditorRef } from 'lib-knowledge-document-structure/plateHooks';
import {
  MyEditor,
  MyVideoEmbedElement,
} from 'lib-knowledge-document-structure/plateTypes';
import { Path } from 'lib-knowledge-document-structure/slate/package';
import { useSelected } from 'lib-knowledge-document-structure/slate/react';
import { useMemo } from 'react';

import { Caption } from '../../caption';
import { ResizeHandle } from '../../resizeHandle';
import resizeStyles from '../../resizeHandle/resizeHandle.module.scss';
import { SortableBlockWrapper } from '../baseBlock';
import s from '../baseBlock/block.module.scss';
import { UploadFormProps, VideoBlockProps } from './videoBlock';

type VideoBlockValueProps = VideoBlockProps & UploadFormProps;

function VideoBlockValue({
  element,
  attributes,
  openUploadMenu,
  uploadRef,
  children,
}: VideoBlockValueProps): JSX.Element {
  const editor = useMyEditorRef();
  const { readOnly } = useEditableProps();
  const selected = useSelected();
  const path = findNodePath(editor, element);
  if (!path) throw new Error('VideoBlockValue path is not defined'); // this cant happen, path should always be defined

  const { align = 'center', provider } = element;

  const alignClass = cx({
    [s['blockAlignStretch']]: align === 'stretch',
    [s['blockAlignCenter']]: align === 'center',
    [s['blockAlignEnd']]: align === 'end',
    [s['blockAlignStart']]: align === 'start',
  });

  const containerClass = cx({
    [s['videoProviderYoutube']]: provider === 'youtube',
    [s['videoProviderVimeo']]: provider === 'vimeo',
    [s['videoProviderLoom']]: provider === 'loom',
  });

  const blockActions = useMemo(
    () => createBlockActions({ editor, element, openUploadMenu, path }),
    [editor, element, openUploadMenu, path],
  );
  return (
    <ResizableProvider>
      <SortableBlockWrapper
        className={cx(
          s['blockVideoEmbedValue'],
          resizeStyles['resizeContainer'],
          selected && resizeStyles['resizeContainerHoverFocused'],
        )}
        element={element}
        blockActions={blockActions}
      >
        <div {...attributes} className={s['blockContent']}>
          <figure
            ref={uploadRef}
            contentEditable={false}
            className={s['blockVideoEmbedWrapper']}
          >
            <Resizable
              options={{
                align:
                  align === 'start'
                    ? 'left'
                    : align === 'end'
                      ? 'right'
                      : 'center',
                readOnly: readOnly || align === 'stretch',
              }}
              className={cx(
                s['blockVideoEmbedPreview'],
                alignClass,
                selected && s['focused'],
              )}
            >
              {!readOnly && align !== 'stretch' && <ResizeHandle side="left" />}
              <div className={containerClass}>
                <iframe
                  src={element.url!}
                  title={element.url!}
                  allowFullScreen
                />
              </div>
              {!readOnly && align !== 'stretch' && (
                <ResizeHandle side="right" />
              )}
            </Resizable>

            <Caption align={align} />
          </figure>
          {children}
        </div>
      </SortableBlockWrapper>
    </ResizableProvider>
  );
}

function createBlockActions({
  editor,
  element,
  openUploadMenu,
  path,
}: {
  editor: MyEditor;
  element: MyVideoEmbedElement;
  openUploadMenu: () => void;
  path: Path;
}) {
  return [
    ...basicBlockActions,
    {
      key: 'replace',
      icon: 'video' as const,
      label: 'Edit',
      isSelected: false,
      action: openUploadMenu,
    },
    {
      key: 'caption',
      icon: 'sms' as const,
      label: 'Caption',
      isSelected: false,
      action: () => {
        select(editor, path);
        setTimeout(() => {
          captionGlobalStore.set.focusEndCaptionPath(path);
        }, 0);
      },
    },
    {
      key: 'heading',
      label: 'Alignment',
    },
    {
      key: 'stretch',
      icon: 'align-justify',
      label: 'Stretch',
      isSelected: element.align === 'stretch',
      action: () => {
        setNodes(editor, { align: 'stretch' }, { at: path });
      },
    },
    {
      key: 'start',
      icon: 'align-left',
      label: 'Left',
      isSelected: element.align === 'start',
      action: () => {
        setNodes(editor, { align: 'start' }, { at: path });
      },
    },
    {
      key: 'end',
      icon: 'align-right',
      label: 'Right',
      isSelected: element.align === 'end',
      action: () => {
        setNodes(editor, { align: 'end' }, { at: path });
      },
    },
    {
      key: 'center',
      icon: 'align-center',
      label: 'Center',
      isSelected: element.align === 'center',
      action: () => {
        setNodes(editor, { align: 'center' }, { at: path });
      },
    },
  ] as const;
}

export default VideoBlockValue;
